import React, { useState, useEffect } from "react";
import {
  Button,
  Spinner,
  Card,
  CardBody,
  Col,
  Form,
  Input,
  Label,
  Row,
  FormFeedback,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import DataTable from "react-data-table-component";
import {
  Add,
  AddLanguageTitle,
  EditLanguageTitle,
  LanguageTitle,
  Submit,
} from "../../../Utils/Constants";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios, { AxiosError } from "axios";
import { baseUrl, mediaUrl, uploadFile } from "../../../Utils/FileUpload";
import { Language } from "../../../Types/Tables.type";
import FilterComponent from "../../../Components/Tables/DataTables/Common/FilterComponent";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Btn } from "../../../AbstractElements";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { useDebouncedCallback } from "use-debounce";
import useCustomToasts from "../../../hooks/useCustomToast";
import { useAuth } from "../../../Context/AuthContext";

interface FormValues {
  language: string;
  icon: FileList | null;
}
interface ErrorResponse {
  message: string;
}
export default function ViewLanguage() {
  const [filterText, setFilterText] = useState("");
  const [filteredItems, setFilteredItems] = useState<Language[]>([]);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortBy, setSortBy] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const [disabledDelete, setDisabledDelete] = useState<string | null>(null);
  const [disableAllDelete, setDisableAllDelete] = useState(false);
  const token = localStorage.getItem("token");
  const [disabledEnable, setDisabledEnable] = useState<string | null>(null);
  const [disableAllEnable, setDisableAllEnable] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { SuccessToast, ErrorToast } = useCustomToasts(navigate);
  const { hasPermission } = useAuth();
  const [existingFile, setExistingFile] = useState<string | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      language: "",
      icon: null,
    },
  });

  const fetchData = async (
    page = currentPage,
    currentPerPage = perPage,
    search = filterText,
    sortField = sortBy,
    sortDir = sortOrder,
    silent = false
  ) => {
    if (!silent) {
      setLoading(true);
    }
    try {
      const response = await axios.get(`${baseUrl}language/get_all_paginated`, {
        params: {
          page,
          per_page: currentPerPage,
          search: search.trim() !== "" ? search : undefined,
          sort_by: sortField !== "" ? sortField : undefined,
          sort_order: sortDir,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data && Array.isArray(response.data.data)) {
        setFilteredItems(response.data.data);
        setTotalRows(response.data.total);
        setCurrentPage(response.data.current_page);
      } else if (Array.isArray(response.data)) {
        setFilteredItems(response.data);
        setTotalRows(response.data.length);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchData(currentPage, perPage, filterText, sortBy, sortOrder);
  }, [refresh]);

  useEffect(() => {
    if (location.state?.id) {
      setEditingId(location.state.id);
      const fetchLanguageById = async () => {
        try {
          const result = await axios.post(
            `${baseUrl}language/viewbyid`,
            { id: location.state.id },
            {
              headers: {
                "Content-Type": "multipart/form-data",
                Authorization: `Bearer ${token}`,
              },
            }
          );
          setValue("language", result.data.language);
          setExistingFile(result.data.icon);
        } catch (error) {
          console.error("Error fetching language details:", error);
        }
      };
      fetchLanguageById();
    }
  }, [location.state?.id, setValue, token]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchData(page, perPage, filterText, sortBy, sortOrder);
  };

  const handlePerRowsChange = async (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
    fetchData(1, newPerPage, filterText, sortBy, sortOrder);
  };

  const handleSort = (column: any, sortDirection: string) => {
    const sortField = column.sortField || "language";
    setSortBy(sortField);
    setSortOrder(sortDirection as "asc" | "desc");
    fetchData(1, perPage, filterText, sortField, sortDirection as "asc" | "desc");
  };

  const debouncedFetch = useDebouncedCallback(
    (searchVal: string, pageVal: number, perPageVal: number, sortVal: string, sortDirVal: "asc" | "desc") => {
      fetchData(pageVal, perPageVal, searchVal, sortVal, sortDirVal);
    },
    400
  );

  const handleFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFilterText(value);
    setCurrentPage(1);
    debouncedFetch(value, 1, perPage, sortBy, sortOrder);
  };

  const handleEdit = (row: Language) => {
    setEditingId(row.id);
    setValue("language", row.language);
    setExistingFile(row.icon);
    clearErrors();
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    reset({ language: "", icon: null });
    setExistingFile(null);
    setFileInputKey((prev) => prev + 1);
    clearErrors();
  };

  const handleDelete = (id: string) => {
    setDisabledDelete(id);
    setDisableAllDelete(true);
    const confirmToast = () =>
      toast.warn(
        <div>
          <div>Are you sure you want to delete this language?</div>
          <div className="d-flex justify-content-end mt-2">
            <Button color="danger" onClick={() => confirmDelete(id)}>
              Yes
            </Button>
            <Button
              color="secondary"
              onClick={() => {
                setDisabledDelete(null);
                setDisableAllDelete(false);
                toast.dismiss();
              }}
              className="ms-2"
            >
              Cancel
            </Button>
          </div>
        </div>,
        {
          position: "top-right",
          autoClose: false,
          closeOnClick: false,
          closeButton: false,
          draggable: false,
          onClose: () => {
            setDisableAllDelete(false);
            setDisabledDelete(null);
          },
        }
      );

    const confirmDelete = async (id: string) => {
      setDisabledDelete(null);
      setDisableAllDelete(false);
      toast.dismiss();
      try {
        await axios.post(
          `${baseUrl}language/delete`,
          { id },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toast.success(`Language Deleted Successfully`, {
          position: "top-right",
          autoClose: 1000,
          closeOnClick: true,
        });
        setFilteredItems((prevItems) =>
          prevItems.filter((item) => item.id !== id)
        );
        if (editingId === id) {
          handleCancelEdit();
        }
        fetchData(currentPage, perPage, filterText, sortBy, sortOrder, true);
      } catch (error) {
        console.error("Error deleting language:", error);
        toast.error("An error occurred while deleting the language", {
          position: "top-right",
          autoClose: 1000,
          closeOnClick: true,
        });
      }
    };

    confirmToast();
  };

  const toggleEnable = (id: string, currentStatus: number) => {
    setDisabledEnable(id);
    setDisableAllEnable(true);

    const confirmToggle = () => {
      toast.warn(
        <div>
          <div>
            Are you sure you want to{" "}
            <strong>{currentStatus === 1 ? "disable" : "enable"}</strong> this
            language?
          </div>
          <div className="d-flex justify-content-end mt-2">
            <Button
              color={currentStatus === 1 ? "danger" : "success"}
              onClick={() => confirmToggleEnable(id, currentStatus)}
            >
              Yes
            </Button>
            <Button
              color="secondary"
              onClick={() => {
                setDisabledEnable(null);
                setDisableAllEnable(false);
                toast.dismiss();
              }}
              className="ms-2"
            >
              Cancel
            </Button>
          </div>
        </div>,
        {
          position: "top-right",
          autoClose: false,
          closeOnClick: false,
          closeButton: false,
          draggable: false,
          onClose: () => {
            setDisabledEnable(null);
            setDisableAllEnable(false);
          },
        }
      );
    };

    const confirmToggleEnable = async (id: string, currentStatus: number) => {
      setDisabledEnable(null);
      setDisableAllEnable(false);
      toast.dismiss();

      try {
        const newStatus = currentStatus === 1 ? 0 : 1;

        await axios.post(
          `${baseUrl}language/toggle_active`,
          { id, is_active: newStatus },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        toast.success(
          `Language ${newStatus === 1 ? "enabled" : "disabled"} successfully!`,
          {
            position: "top-right",
            autoClose: 1000,
            closeOnClick: true,
          }
        );
        fetchData(currentPage, perPage, filterText, sortBy, sortOrder, true);
      } catch (error) {
        console.error("Error toggling language:", error);
        toast.error("Error updating enable status", {
          position: "top-right",
          autoClose: 1000,
        });
      }
    };

    confirmToggle();
  };

  const languageTableColumns = [
    {
      name: "Sl. No.",
      cell: (_row: Language, index: number) =>
        (currentPage - 1) * perPage + index + 1,
      width: "12%",
    },
    {
      name: "Icon",
      cell: (row: Language) => (
        <img
          src={`${mediaUrl}/${row.icon}`}
          alt={row.icon}
          width={25}
          height={25}
          style={{
            objectFit: "cover",
            borderRadius: "10px",
          }}
        />
      ),
      width: "18%",
    },
    {
      name: "Language",
      selector: (row: Language) => row.language,
      sortable: true,
      sortField: "language",
      width: "25%",
    },

    {
      name: "Status",
      cell: (row: Language) => (
        <span
          className={`badge rounded-pill ${
            row.is_active === 1 ? "bg-success" : "bg-danger"
          }`}
        >
          {row.is_active === 1 ? "Enable" : "Disable"}
        </span>
      ),
      sortable: true,
      sortField: "is_active",
      width: "20%",
    },
    {
      name: "Actions",
      cell: (row: Language) => (
        <div className="d-flex justify-content-between">
          {hasPermission("language.enable_disable") && (
            <Button
              color={row.is_active ? "success" : "secondary"}
              size="sm"
              onClick={() => toggleEnable(row.id, row.is_active)}
              disabled={disabledEnable === row.id || disableAllEnable}
              style={{
                marginRight: "5px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              title={row.is_active ? "Disable" : "Enable"}
            >
              <i
                className={`fa ${row.is_active ? "fa-check-circle" : "fa-ban"}`}
              />
            </Button>
          )}
          {hasPermission("language.edit") && (
            <Button
              color="primary"
              size="sm"
              onClick={() => handleEdit(row)}
              style={{
                marginRight: "5px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              title="Edit"
            >
              <i className="icon-pencil-alt" />
            </Button>
          )}
          {hasPermission("language.delete") && (
            <Button
              color="danger"
              size="sm"
              onClick={() => handleDelete(row.id)}
              disabled={disabledDelete === row.id || disableAllDelete}
              style={{
                marginRight: "5px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              title="Delete"
            >
              <i className="icon-trash" />
            </Button>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      width: "25%",
    },
  ];

  const customStyles = {
    headCells: {
      style: {
        fontSize: "16px",
        fontWeight: "600",
        color: "#308e87",
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #308e87",
      },
    },
    rows: {
      style: {
        borderBottom: "1px solid #dee2e6",
      },
    },
    table: {
      style: {
        border: "1px solid #dee2e6",
        borderRadius: "8px",
      },
    },
  };
  const onSubmit = async (data: FormValues) => {
    setIsButtonDisabled(true);

    try {
      let filename = existingFile || "";

      if (data.icon && data.icon instanceof FileList && data.icon.length > 0) {
        const file = data.icon[0];
        filename = await uploadFile(file);
      }

      if (!editingId) {
        if (!filename) {
          toast.error("Please upload an icon.");
          setIsButtonDisabled(false);
          return;
        }

        const payload = {
          language: data.language,
          icon: filename,
        };

        const response = await axios.post(`${baseUrl}language/store`, payload, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.status === 200 || response.status === 201) {
          reset({ language: "", icon: null });
          setExistingFile(null);
          setFileInputKey((prev) => prev + 1);
          toast.success("Language added successfully!", {
            position: "top-right",
            autoClose: 1000,
            closeOnClick: true,
          });
          setCurrentPage(1);
          fetchData(1, perPage, filterText, sortBy, sortOrder, true);
        } else {
          ErrorToast("Failed to add language");
        }
      } else {
        const payload = {
          id: editingId,
          language: data.language,
          icon: filename,
        };

        const response = await axios.post(`${baseUrl}language/update`, payload, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.status === 200 || response.status === 201) {
          reset({ language: "", icon: null });
          setEditingId(null);
          setExistingFile(null);
          setFileInputKey((prev) => prev + 1);
          toast.success("Language updated successfully!", {
            position: "top-right",
            autoClose: 1000,
            closeOnClick: true,
          });
          fetchData(currentPage, perPage, filterText, sortBy, sortOrder, true);
        } else {
          ErrorToast("Failed to update language");
        }
      }
    } catch (err) {
      const axiosError = err as AxiosError;
      const errorMessage =
        (axiosError.response?.data as ErrorResponse)?.message ||
        "An unexpected error occurred";

      console.error("Error:", errorMessage);
      ErrorToast(errorMessage);
    } finally {
      setTimeout(() => {
        setIsButtonDisabled(false);
      }, 1000);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };
  return (
    <>
      <Breadcrumbs
        pageTitle={editingId ? EditLanguageTitle : AddLanguageTitle}
        parent={LanguageTitle}
        title={editingId ? EditLanguageTitle : AddLanguageTitle}
      />
      <Row>
        {(hasPermission("language.add") || (editingId && hasPermission("language.edit"))) && (
          <Col xl={hasPermission("language.view") ? 4 : 12} className="mb-lg-3">
            <Card>
              <CardBody className="custom-input form-validation">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">
                    {editingId ? EditLanguageTitle : AddLanguageTitle}
                  </h5>
                  {editingId && (
                    <span className="badge bg-warning text-dark">Edit Mode</span>
                  )}
                </div>
                <Form onSubmit={handleSubmit(onSubmit)}>
                  <Row className="g-3">
                    <Col xs={12}>
                      <Label htmlFor="language-name">Language</Label>
                      <Controller
                        name="language"
                        control={control}
                        rules={{
                          required: "Language is required",
                          maxLength: {
                            value: 30,
                            message:
                              "Language cannot be more than 30 characters.",
                          },
                          pattern: {
                            value: /^[A-Za-z\s\W_]+$/,
                            message:
                              "Only letters, spaces, and symbols are allowed. Numbers are not allowed.",
                          },
                          validate: (value) => {
                            if (/\d/.test(value)) {
                              return "Numbers are not allowed.";
                            }
                            if (value.trim() === "") {
                              return "Language cannot be empty or only spaces.";
                            }
                            return true;
                          },
                        }}
                        render={({ field }) => (
                          <>
                            <Input
                              id="language-name"
                              type="text"
                              placeholder="Language"
                              {...field}
                              invalid={!!errors.language}
                              style={{
                                color: "var(--input-text-color)",
                                borderColor: errors.language ? "red" : "",
                              }}
                              autoComplete="off"
                              onKeyDown={handleKeyDown}
                            />
                            {errors.language && (
                              <FormFeedback>
                                {errors.language.message}
                              </FormFeedback>
                            )}
                          </>
                        )}
                      />
                    </Col>

                    <Col xs={12}>
                      <Label htmlFor="formFile">Icon</Label>
                      <Controller
                        name="icon"
                        control={control}
                        rules={{
                          required:
                            !editingId && !existingFile
                              ? "Icon is required"
                              : false,
                        }}
                        render={({ field: { onChange, onBlur, ref } }) => (
                          <>
                            <Input
                              key={fileInputKey}
                              id="formImage"
                              type="file"
                              accept="image/*"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  onChange(e.target.files);
                                  // Update the image preview immediately
                                  const reader = new FileReader();
                                  reader.onloadend = () => {
                                    setExistingFile(reader.result as string);
                                  };
                                  reader.readAsDataURL(file);
                                }
                              }}
                              onBlur={onBlur}
                              innerRef={ref}
                              invalid={!!errors.icon}
                              style={{ borderColor: errors.icon ? "red" : "" }}
                              onKeyDown={handleKeyDown}
                            />
                            {errors.icon && (
                              <FormFeedback>{errors.icon.message}</FormFeedback>
                            )}
                            {existingFile && (
                              <img
                                src={
                                  existingFile.startsWith("data:")
                                    ? existingFile
                                    : `${mediaUrl}/${existingFile}`
                                }
                                alt="Preview"
                                width="70"
                                height="70"
                                style={{ marginTop: "5px", borderRadius: "10px" }}
                              />
                            )}
                          </>
                        )}
                      />
                    </Col>

                    <Col xs={12} className="mt-3 d-flex gap-2">
                      <Btn color="primary" disabled={isButtonDisabled}>
                        {editingId ? "Update" : Submit}
                      </Btn>
                      {editingId && (
                        <Button
                          type="button"
                          color="secondary"
                          onClick={handleCancelEdit}
                        >
                          Cancel
                        </Button>
                      )}
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>
        )}
        {hasPermission("language.view") && (
          <Col xl={hasPermission("language.add") || hasPermission("language.edit") ? 8 : 12} className="mb-lg-3">
            <Card>
              <CardBody>
                <div className="mb-3">
                  <FilterComponent
                    onFilter={handleFilter}
                    filterText={filterText}
                  />
                </div>
                <div className="table-responsive">
                  <DataTable
                    data={filteredItems}
                    columns={languageTableColumns}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={perPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                    paginationDefaultPage={currentPage}
                    sortServer
                    onSort={handleSort}
                    progressPending={loading}
                    progressComponent={
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          minHeight: "200px",
                          width: "100%",
                          padding: "20px 0",
                        }}
                      >
                        <Spinner color="primary" />
                      </div>
                    }
                    highlightOnHover
                    striped
                    customStyles={customStyles}
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
        )}
      </Row>
      <ToastContainer />
    </>
  );
}
